import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowRight, BookOpen, CheckCircle, Clock } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HomePage = () => {
  const [parcours, setParcours] = useState([]);
  const [quizResults, setQuizResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, logEvent } = useAuth();

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [parcoursRes, quizRes] = await Promise.all([
        axios.get(`${API}/parcours`),
        user ? axios.get(`${API}/quiz/results/${user.id}`) : Promise.resolve({ data: [] })
      ]);
      setParcours(parcoursRes.data);
      setQuizResults(quizRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getParcoursStatus = (parcoursId) => {
    const result = quizResults.find(r => r.parcours_id === parcoursId);
    if (result?.passed) return 'completed';
    if (result) return 'in-progress';
    return 'not-started';
  };

  const completedCount = quizResults.filter(r => r.passed).length;
  const inProgressCount = quizResults.filter(r => !r.passed).length;

  return (
    <div className="min-h-screen bg-[#f2f2f2] flex flex-col" data-testid="home-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="bg-[#571925] text-white py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            {/* Logo VIP Plus */}
            <div className="mb-6">
              <img 
                src="/VIP.png" 
                alt="VIP Plus" 
                className="h-12 md:h-16 w-auto"
              />
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 font-['Outfit']">
              Bienvenue, {user?.email?.split('@')[0] || 'Utilisateur'} !
            </h1>
            <p className="text-lg md:text-xl text-white/80 mb-8">
              Développez vos compétences Salesforce à travers nos parcours de formation interactifs.
            </p>
            
            {/* Progress Stats */}
            <div className="grid grid-cols-3 gap-4 max-w-lg">
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <div className="text-2xl md:text-3xl font-bold">{completedCount}</div>
                <div className="text-sm text-white/70">Complétés</div>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <div className="text-2xl md:text-3xl font-bold">{inProgressCount}</div>
                <div className="text-sm text-white/70">En cours</div>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <div className="text-2xl md:text-3xl font-bold">{9 - completedCount - inProgressCount}</div>
                <div className="text-sm text-white/70">À découvrir</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Parcours Grid */}
      <section className="flex-1 py-12">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#571925] font-['Outfit']">
              Parcours de formation
            </h2>
            <span className="text-sm text-gray-500">{parcours.length} parcours disponibles</span>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {[...Array(9)].map((_, i) => (
                <Card key={i} className="overflow-hidden">
                  <Skeleton className="h-48 w-full" />
                  <CardContent className="p-6">
                    <Skeleton className="h-6 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-full mb-4" />
                    <Skeleton className="h-10 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {parcours.map((p, index) => {
                const status = getParcoursStatus(p.id);
                return (
                  <Link 
                    key={p.id} 
                    to={`/vipplus/parcours/${p.id}`}
                    onClick={() => logEvent('parcours_opened', p.id)}
                    data-testid={`parcours-card-${p.id}`}
                  >
                    <Card className="course-card group relative overflow-hidden rounded-xl bg-white border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 h-full">
                      {/* Status Badge */}
                      {status === 'completed' && (
                        <div className="absolute top-4 right-4 z-10 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Validé
                        </div>
                      )}
                      {status === 'in-progress' && (
                        <div className="absolute top-4 right-4 z-10 bg-[#ff9a52] text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          En cours
                        </div>
                      )}

                      {/* Image */}
                      <div className="relative h-48 overflow-hidden">
                        <img 
                          src={p.image} 
                          alt={p.title}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                        <div className="absolute bottom-4 left-4">
                          <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs">
                            Parcours {index + 1}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <CardContent className="p-6">
                        <h3 className="text-lg font-semibold text-[#1a1a1a] mb-2 group-hover:text-[#cc5759] transition-colors">
                          {p.title}
                        </h3>
                        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                          {p.description}
                        </p>
                        <Button 
                          className="w-full bg-[#ff9a52] hover:bg-[#e08540] text-white rounded-full font-medium"
                          data-testid={`start-parcours-${p.id}`}
                        >
                          <BookOpen className="h-4 w-4 mr-2" />
                          {status === 'completed' ? 'Revoir' : status === 'in-progress' ? 'Continuer' : 'Commencer'}
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a1a1a] text-white py-8 mt-auto">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <p className="text-sm text-gray-400">
            © {new Date().getFullYear()} VIP Plus Academy - Plateforme de formation Salesforce
          </p>
        </div>
      </footer>
    </div>
  );
};
